// The fake api's owners' daily summary (N-27, F-ANL-07):
//
//   GET  /v1/organizations/{o}/reports/daily-summary?day&venueId   reports:financial and the owner role
//   POST /v1/organizations/{o}/reports/daily-summary/send          the same; records what was asked
//
// Deterministic figures for any business day — the two fake venues, Playground Central and Playground North, the
// latter not closed yet on the default day — so the console's tests, the axe scan and the screenshots read a realistic
// summary. Money is integer paise with its currency, as the server sends it.

import type { DailySummary, DailySummaryKpis, DailySummaryVenue } from '@clavian/api-client';

/** The latest business day ended everywhere, as the server would answer with no `day`. */
export const FAKE_DAILY_SUMMARY_DAY = '2026-09-30';

const CENTRAL = { venueId: 'ven_01k5znw4hmfay8vvr14d2pf2dc', name: 'Playground Central', scale: 1 };
const NORTH = { venueId: 'ven_01k5znw4hmfay8vvr14d2pf2dd', name: 'Playground North', scale: 0.62 };

const INR = (amount: number) => ({ amount: Math.round(amount), currency: 'INR' });
const change = (direction: 'up' | 'down' | 'flat', basisPoints: number | null) => ({ direction, basisPoints });

/** A stable 0 … 1 for a key. */
function noise(key: string): number {
  let hash = 2166136261;
  for (const char of key) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return ((hash >>> 0) % 10_000) / 10_000;
}

function kpis(day: string, scale: number): DailySummaryKpis {
  const wobble = 0.85 + noise(`${day}:${scale}`) * 0.3;
  const net = Math.round(3_200_000 * scale * wobble);
  const cash = Math.round(net * 0.42);
  const upi = Math.round(net * 0.31);
  const card = Math.round(net * 0.09);
  const wallet = net - cash - upi - card;
  const sessions = Math.round(46 * scale * wobble);
  const playSeconds = sessions * 5_100;
  const stations = Math.round(24 * scale);
  const available = stations * 16 * 3_600;
  return {
    takings: {
      grossSales: INR(net * 1.04),
      discounts: INR(net * 0.03),
      refunds: INR(21_000),
      refundCount: 1,
      tax: INR(net * 0.06),
      netTakings: INR(net),
      moneyIn: INR(net + 150_000),
      byTender: [
        { tender: 'cash', amount: INR(cash) },
        { tender: 'counter_card', amount: INR(card) },
        { tender: 'counter_upi', amount: INR(upi) },
        { tender: 'online', amount: INR(0) },
        { tender: 'wallet', amount: INR(wallet) },
      ],
      onAccount: INR(15_000),
    },
    play: {
      sessions,
      playSeconds,
      averageSessionSeconds: 5_100,
      inSessionSeconds: playSeconds,
      availableSeconds: available,
      utilisationBasisPoints: Math.round((playSeconds * 10_000) / available),
      stations,
      revenuePerStation: INR(net / stations),
      passSales: INR(net * 0.28),
      payAsYouGo: INR(net * 0.37),
    },
    food: {
      foodSales: INR(net * 0.18),
      retailSales: INR(net * 0.04),
      orders: Math.round(31 * scale),
      topItems: [
        { name: 'Cold coffee', kind: 'food', quantity: Math.round(18 * scale), net: INR(180_00 * 18 * scale) },
        { name: 'Veg burger', kind: 'food', quantity: Math.round(11 * scale), net: INR(160_00 * 11 * scale) },
        { name: 'Energy drink', kind: 'retail', quantity: Math.round(9 * scale), net: INR(120_00 * 9 * scale) },
      ],
      stockAlerts: scale >= 1 ? 2 : null,
    },
    wallet: { topUps: INR(150_000 * scale), bonusIssued: INR(15_000 * scale), spent: INR(wallet), onAccountOwed: INR(45_000) },
    cash: {
      expected: INR(cash + 200_000),
      counted: INR(cash + 200_000 - (scale >= 1 ? 70_000 : 0)),
      variance: INR(scale >= 1 ? -70_000 : 0),
      drawers: [
        {
          shiftId: scale === 1 ? 'shf_01k5znw4hmfay8vvr14d2pf9a1' : 'shf_01k5znw4hmfay8vvr14d2pf9a2',
          counterName: 'Front desk',
          status: 'closed',
          expected: INR(cash + 200_000),
          counted: INR(cash + 200_000 - (scale >= 1 ? 70_000 : 0)),
          variance: INR(scale >= 1 ? -70_000 : 0),
        },
      ],
      openShifts: 0,
    },
    players: { unique: Math.round(41 * scale), new: Math.round(6 * scale) },
  } as DailySummaryKpis;
}

function venueOf(venue: typeof CENTRAL, day: string, closed: boolean): DailySummaryVenue {
  const figures = kpis(day, venue.scale);
  const net = figures.takings.netTakings.amount;
  return {
    venueId: venue.venueId,
    name: venue.name,
    businessDay: day,
    timeZone: 'Asia/Kolkata',
    currency: 'INR',
    dayClosed: closed,
    ...(closed ? { closeMode: 'manual' as const } : {}),
    closedAt: closed ? `${day}T20:30:00.000Z` : null,
    kpis: figures,
    moneyTrends: [{ metric: 'netTakings', current: INR(net), lastWeek: INR(net / 1.12), average: INR(net / 1.05), vsLastWeek: change('up', 1_200), vsAverage: change('up', 500) }],
    trends: [{ metric: 'sessions', unit: 'count', current: figures.play.sessions, lastWeek: figures.play.sessions + 4, average: figures.play.sessions - 1, vsLastWeek: change('down', 800), vsAverage: change('up', 200) }],
  } as DailySummaryVenue;
}

/** One day's summary: the organisation's (both venues) or one venue's. */
export function fakeDailySummary(day: string = FAKE_DAILY_SUMMARY_DAY, venueId?: string, organizationName = 'Playground Gaming'): DailySummary {
  const venues = venueId === undefined ? [venueOf(CENTRAL, day, true), venueOf(NORTH, day, false)] : [venueOf(venueId === NORTH.venueId ? NORTH : CENTRAL, day, true)];
  const totalNet = venues.reduce((sum, venue) => sum + venue.kpis.takings.netTakings.amount, 0);
  const totals = venues.length === 1 ? (venues[0] as DailySummaryVenue).kpis : kpis(day, CENTRAL.scale + NORTH.scale);
  const flags = [
    ...(venues.some((venue) => venue.venueId === CENTRAL.venueId) ? [{ kind: 'cash_variance', venueId: CENTRAL.venueId, venueName: CENTRAL.name, amount: INR(-70_000), count: null }] : []),
    ...(venueId === undefined ? [{ kind: 'day_not_closed', venueId: NORTH.venueId, venueName: NORTH.name, amount: null, count: null }] : []),
    ...(venues.some((venue) => venue.venueId === CENTRAL.venueId) ? [{ kind: 'stock_alerts', venueId: CENTRAL.venueId, venueName: CENTRAL.name, amount: null, count: 2 }] : []),
  ];
  return {
    kind: venueId === undefined ? 'organization' : 'venue',
    organizationId: 'org_01k5znw4hmfay8vvr14d2pf2db',
    organizationName,
    businessDay: day,
    currency: 'INR',
    totals,
    moneyTrends: [
      { metric: 'netTakings', current: INR(totalNet), lastWeek: INR(totalNet / 1.12), average: INR(totalNet / 1.05), vsLastWeek: change('up', 1_200), vsAverage: change('up', 500) },
      { metric: 'moneyIn', current: INR(totalNet + 150_000), lastWeek: INR(totalNet), average: INR(totalNet), vsLastWeek: change('up', 300), vsAverage: change('up', 300) },
      { metric: 'foodSales', current: totals.food.foodSales, lastWeek: totals.food.foodSales, average: totals.food.foodSales, vsLastWeek: change('flat', 0), vsAverage: change('flat', 0) },
      { metric: 'refunds', current: INR(21_000), lastWeek: INR(0), average: INR(3_000), vsLastWeek: change('up', null), vsAverage: change('up', 60_000) },
    ],
    trends: [
      { metric: 'sessions', unit: 'count', current: totals.play.sessions, lastWeek: totals.play.sessions + 5, average: totals.play.sessions - 2, vsLastWeek: change('down', 700), vsAverage: change('up', 300) },
      { metric: 'playSeconds', unit: 'seconds', current: totals.play.playSeconds, lastWeek: totals.play.playSeconds - 9_000, average: totals.play.playSeconds - 4_000, vsLastWeek: change('up', 400), vsAverage: change('up', 200) },
      { metric: 'utilisation', unit: 'basis_points', current: totals.play.utilisationBasisPoints, lastWeek: totals.play.utilisationBasisPoints, average: 1_900, vsLastWeek: change('flat', 0), vsAverage: change('up', 900) },
      { metric: 'uniquePlayers', unit: 'count', current: totals.players.unique, lastWeek: totals.players.unique - 6, average: totals.players.unique - 3, vsLastWeek: change('up', 1_500), vsAverage: change('up', 800) },
      { metric: 'newPlayers', unit: 'count', current: totals.players.new, lastWeek: 4, average: 5, vsLastWeek: change('up', 2_500), vsAverage: change('up', 2_000) },
    ],
    venues,
    bestVenueId: venueId === undefined ? CENTRAL.venueId : null,
    flags,
    generatedAt: `${day}T23:30:00.000Z`,
    trigger: venueId === undefined ? 'deadline' : 'closed',
    sentAt: `${day}T23:30:00.000Z`,
    partial: false,
  } as DailySummary;
}

export interface FakeDailySummaryRequest {
  readonly pathname: string;
  readonly method: string;
  readonly searchParams: URLSearchParams;
  readonly input: Record<string, unknown>;
  readonly permissions: readonly string[];
  readonly role: string | undefined;
  readonly organizationPath: string;
  /** Where a send is recorded, for a test to read. */
  readonly sent: { day: string; venueId: string | null }[];
}

/** `undefined` for a request that is not one of these routes. */
export function fakeDailySummaryResponse(request: FakeDailySummaryRequest): { readonly status: number; readonly body: unknown } | undefined {
  const { pathname, method, searchParams, permissions, role, organizationPath } = request;
  const read = pathname === `${organizationPath}/reports/daily-summary` && method === 'GET';
  const send = pathname === `${organizationPath}/reports/daily-summary/send` && method === 'POST';
  if (!read && !send) return undefined;
  const forbidden = { status: 403, body: { type: 'https://clavian.io/problems/forbidden', title: 'Only an owner reads the daily summary.', status: 403, code: 'forbidden' } };
  if (!permissions.includes('reports:financial') || role !== 'owner') return forbidden;
  const day = (read ? searchParams.get('day') : (request.input['day'] as string | undefined)) ?? FAKE_DAILY_SUMMARY_DAY;
  const venueId = (read ? searchParams.get('venueId') : (request.input['venueId'] as string | undefined)) ?? undefined;
  if (read) return { status: 200, body: fakeDailySummary(day, venueId ?? undefined) };
  request.sent.push({ day, venueId: venueId ?? null });
  return { status: 202, body: { businessDay: day, kind: venueId === undefined || venueId === null ? 'organization' : 'venue', venueId: venueId ?? null } };
}
